import { STORES_CORRECTION_KINDS, STORES_DOC_TABS } from "@nexseed/shared";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { Scale } from "lucide-react";
import { CorrectionListPage } from "@/features/stores/components/correction-list-page";
import { ChooserGate } from "@/features/stores/components/type-chooser";
import { CORRECTION_TYPE_OPTIONS } from "@/features/stores/type-options";

export const Route = createFileRoute("/_app/stores/corrections/")({
  validateSearch: z.object({
    // No default: the Stores menu lands here with no type and the pop-up asks (user, 2026-10-08).
    kind: z.enum(STORES_CORRECTION_KINDS).optional(),
    tab: z.enum(STORES_DOC_TABS).default("submitted"),
    from: z.string().optional(),
    to: z.string().optional(),
    page: z.number().int().min(1).default(1),
  }),
  beforeLoad: () => {
    if (!useAuth.getState().can("stores.view")) throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  if (!ready) return <ListPageSkeleton />;
  const { kind } = s;
  if (!kind)
    return (
      <ChooserGate
        pageTitle="Stock Corrections"
        breadcrumbs={[{ label: "Stores" }, { label: "Corrections" }]}
        chooserTitle="Transaction: Stock Corrections"
        chooserDescription="What is being corrected?"
        options={CORRECTION_TYPE_OPTIONS}
        icon={Scale}
        onPick={(k) =>
          void navigate({ search: (prev) => ({ ...prev, kind: k, page: 1 }) })
        }
      />
    );
  return (
    <CorrectionListPage
      facilityId={selected?.id}
      search={{ ...s, kind }}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
      onChangeType={() =>
        void navigate({ search: (prev) => ({ ...prev, kind: undefined }) })
      }
    />
  );
}
